import type { BiddleDraft } from '@/types/models'

const NOTES_MAX = 240
const FLEXIBLE = "I'm flexible"

/** Digits-only price string for the create wizard (commas stripped). */
function normalizePrice(raw: string | null): string {
  if (!raw) return ''
  const digits = raw.replace(/[^\d]/g, '')
  if (!digits) return ''
  return Number(digits).toLocaleString('en-AU')
}

/** Settlement days / range / flexible — strip "days" suffix. */
function normalizeSettlement(raw: string | null): {
  settlement: string
  flexibleSettlement: boolean
} {
  if (!raw?.trim()) return { settlement: '60', flexibleSettlement: false }
  const trimmed = raw.trim()
  if (trimmed.toLowerCase().includes('flex')) {
    return { settlement: FLEXIBLE, flexibleSettlement: true }
  }
  const range = trimmed.match(/(\d+)\s*[-–]\s*(\d+)/)
  if (range) {
    return { settlement: `${range[1]}-${range[2]}`, flexibleSettlement: false }
  }
  const single = trimmed.match(/(\d+)/)
  if (single) {
    return { settlement: single[1]!, flexibleSettlement: false }
  }
  return { settlement: trimmed, flexibleSettlement: false }
}

/** Deposit percent / flexible — strip % suffix. */
function normalizeDeposit(raw: string | null): {
  deposit: string
  flexibleDeposit: boolean
} {
  if (!raw?.trim()) return { deposit: '10', flexibleDeposit: false }
  const trimmed = raw.trim()
  if (trimmed.toLowerCase().includes('flex')) {
    return { deposit: FLEXIBLE, flexibleDeposit: true }
  }
  const match = trimmed.match(/(\d+(?:\.\d+)?)/)
  if (match) {
    return { deposit: match[1]!, flexibleDeposit: false }
  }
  return { deposit: trimmed, flexibleDeposit: false }
}

/**
 * Build a synthetic create-wizard draft from `?price=&deposit=&settlement=&notes=`
 * when opening create via deep link (e.g. Claude MCP handoff).
 * Returns null if none of those params are present.
 */
export function createPrefillDraftFromSearchParams(
  propertyId: string,
  fullAddress: string,
  params: URLSearchParams,
): BiddleDraft | null {
  const hasPrice = params.has('price')
  const hasDeposit = params.has('deposit')
  const hasSettlement = params.has('settlement')
  const hasNotes = params.has('notes')
  if (!hasPrice && !hasDeposit && !hasSettlement && !hasNotes) return null

  const { settlement, flexibleSettlement } = normalizeSettlement(
    params.get('settlement'),
  )
  const { deposit, flexibleDeposit } = normalizeDeposit(params.get('deposit'))
  const notesRaw = params.get('notes') ?? ''
  const notes = notesRaw.slice(0, NOTES_MAX)
  const price = normalizePrice(params.get('price'))
  const now = new Date()

  // Claude / deep-link handoff: skip straight to review when we have a price so
  // the user can Confirm & Submit in one click (terms already agreed in chat).
  const jumpToReview = Boolean(price)

  return {
    propertyId,
    fullAddress,
    step: jumpToReview ? 'review' : 'price',
    agreed: jumpToReview,
    price,
    settlement,
    deposit,
    flexibleSettlement,
    flexibleDeposit,
    isOpenOffer: true,
    expiry: '',
    notes,
    sendLetter: false,
    updatedAt: now,
    createdAt: now,
  }
}

/** Query keys used for create-Biddle deep-link prefill. */
export const CREATE_BIDDLE_PREFILL_KEYS = [
  'price',
  'deposit',
  'settlement',
  'notes',
] as const

export function stripCreatePrefillParams(params: URLSearchParams): void {
  for (const key of CREATE_BIDDLE_PREFILL_KEYS) {
    params.delete(key)
  }
}
